import { Box, Flex, Stack } from '@chakra-ui/react';
import { Divider, Grid, gridConfig } from '@time-line/components/common';
import { SocialIcon } from 'components/common/SocialIcon';
import { CountrySelector } from 'components/localisation/CountrySelector';
import { useGlobals } from 'hooks/useGlobals';
import { useIsSkincare } from 'hooks/useIsSkincare';
import { type IndexedPageFootnote } from 'hooks/usePageFootnotes';
import { env } from 'lib/constants/env';
import { buildScienceNav } from 'lib/constants/navigation';
import { SOCIAL_PROFILES } from 'lib/constants/socialProfiles';
import { getTrackingId } from 'utils/getTrackingId';

import { FOOTER_CONTENT } from '../constants';
import { Disclaimers } from '../Disclaimers';
import { Hero } from '../Hero';
import { Meta } from '../Meta';
import { type DisclaimerContent } from '../useDisclaimers';
import { useFooterInputs } from '../useFooterInputs';
import { Nav } from './Nav';
import { NavMobile } from './NavMobile';

const { borderWidthClamp, gridBorderRadius } = gridConfig;

type FooterProps = {
  disclaimers?: DisclaimerContent | null;
  pageFootnotes?: IndexedPageFootnote[];
};

export const Footer = ({
  disclaimers: disclaimersProp,
  pageFootnotes: pageFootnotesProp,
}: FooterProps = {}) => {
  const {
    nav: supplementsNav,
    skincareNav,
    legal: supplementsLegal,
    skincareLegal,
  } = FOOTER_CONTENT;
  const { data: globals } = useGlobals();
  const isSkincare = useIsSkincare();
  const { disclaimers, pageFootnotes } = useFooterInputs({
    disclaimers: disclaimersProp,
    pageFootnotes: pageFootnotesProp,
  });

  const footerHeading = globals?.siteSettings.footerHeading;
  const plpPages = globals?.siteSettings?.plp;
  const scienceNavigation = globals?.siteSettings?.scienceNav?.navigationItems;

  const scienceNav = buildScienceNav(scienceNavigation);

  const socials = SOCIAL_PROFILES;
  const { nav, legal } = isSkincare
    ? { nav: skincareNav, legal: skincareLegal }
    : { nav: [scienceNav, ...supplementsNav], legal: supplementsLegal };

  const footerNav = plpPages ? [plpPages, ...nav] : nav;

  return (
    <Box className="footer" as="footer" color="gray.600">
      <Grid
        pos="relative"
        pb="m"
        pt={{ base: 'm', lg: 'lg' }}
        withBorder
        bgColor="gray.100"
        borderRadius={gridBorderRadius}
        mb={borderWidthClamp}
      >
        <Hero heading={footerHeading} />
        <Grid.Inner mb={{ base: 'lg', lg: 'm' }}>
          <Box gridColumn="span 12">
            <Box display={{ base: 'none', lg: 'block' }}>
              <Nav nav={footerNav} columns={isSkincare ? 4 : 5} />
            </Box>
            <Box display={{ base: 'block', lg: 'none' }}>
              <NavMobile nav={footerNav} />
            </Box>
          </Box>
        </Grid.Inner>

        <Grid.Inner alignItems="center" mb="2xs" gridRowGap="lg">
          <Flex
            gridColumn="span 12"
            justifyContent="space-between"
            gap="4xs"
            flexWrap="wrap"
          >
            {env.NEXT_PUBLIC_COUNTRY_SELECTOR_ENABLED && (
              <CountrySelector showFullName variant="overline" />
            )}
            <Stack
              align="center"
              direction="row"
              justifyContent={{ base: 'start', lg: 'end' }}
              gap="2xs"
            >
              {socials.map((social, i) => (
                <SocialIcon
                  key={i}
                  as="a"
                  id={getTrackingId(social.name, { parent: 'footer' })}
                  href={social.href}
                  aria-label={social.label}
                  target="_blank"
                  rel="noreferrer"
                >
                  {social.icon}
                </SocialIcon>
              ))}
            </Stack>
          </Flex>
        </Grid.Inner>
        <Grid.Inner mb="sm">
          <Box gridColumn="span 12">
            {disclaimers || pageFootnotes.length > 0 ? (
              <Disclaimers
                disclaimers={disclaimers}
                pageFootnotes={pageFootnotes}
              />
            ) : (
              <Divider addOpacity />
            )}
          </Box>
        </Grid.Inner>
        <Meta legal={legal} />
      </Grid>
    </Box>
  );
};
